import { getLocalName } from 'vet-bones/utils';

import {
  MappingIdentifier,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';

export const getFileUploadAttributeBinding = (
  fieldValue: string,
  rangeAbbreviation: string,
  fieldVariable: string
) => {
  // the `sourceField` function returns of the name of the source field referred to by its
  // string argument (especially important because headers might use periods or other symbols)
  return `BIND(StrDt(sourceField("${escapeDoubleQuotes(
    fieldValue
  )}"), ${rangeAbbreviation}) AS ${fieldVariable})`;
};

export const getVirtualGraphAttributeBinding = (
  fieldLabel: string,
  rangeAbbreviation: string,
  fieldVariable: string
) => {
  return `BIND(StrDt(?${fieldLabel}, ${rangeAbbreviation}) AS ${fieldVariable})`;
};

export const getFileUploadLabelBinding = (
  stringFnName: string,
  mappingLabelValue: string,
  labelVariable: string
) => {
  return `BIND(${stringFnName}(sourceField("${escapeDoubleQuotes(
    mappingLabelValue
  )}")) AS ${labelVariable})`;
};

/**
 * Returns a curried function that returns a unique variable for
 * a class iri, and a binding for the SMS file if the unique variable did not previous exist
 * (in order to avoid duplicate bindings for the same class)
 */
export const getBindingAndVariableFunction = ({
  dataNamespace,
  getStringifiedLabel,
  getUniqueVariable,
}: {
  dataNamespace: string;
  getStringifiedLabel: (iri: string) => string;
  getUniqueVariable: (identifier: string) => string;
}) => {
  const classVariablesByIriAndIdentifier: Record<string, string> = {};
  return (classIri: string, identifier: MappingIdentifier) => {
    const key = `${classIri}-${identifier.baseIri}-${identifier.parts
      .map((part) => part.value)
      .join('-')}`;

    if (classVariablesByIriAndIdentifier[key]) {
      return {
        binding: null,
        classIri,
        identifierVariable: classVariablesByIriAndIdentifier[key],
      };
    }

    const classLabel = getStringifiedLabel(classIri);
    const compositeIdentifierString = identifier.parts.reduce<string>(
      (acc, part) =>
        part.type === MappingIdentifierType.DELIMITER
          ? `${acc}${part.value}`
          : `${acc}{${escapeDoubleQuotes(part.value)}}`,
      ''
    );
    const identifierVariable = getUniqueVariable(`?${classLabel}_iri`);
    // eslint-disable-next-line no-param-reassign
    classVariablesByIriAndIdentifier[key] = identifierVariable;

    const baseIri =
      identifier.baseIri ||
      (dataNamespace ? `${dataNamespace}${getLocalName(classIri)}:` : '');

    const binding = baseIri
      ? `BIND(TEMPLATE("${baseIri}${compositeIdentifierString}") AS ${identifierVariable})`
      : `BIND(TEMPLATE("${classIri}-${compositeIdentifierString}") AS ${identifierVariable})`;

    return { binding, identifierVariable };
  };
};
